import { useEffect } from "react";
import { useInsightsStore } from "@/state/insightsStore";
import { KpiCard } from "@/components/insights/KpiCard";
import { InsightsRangePicker } from "@/components/insights/InsightsRangePicker";
import { InsightsLineChart } from "@/components/insights/InsightsLineChart";
import { StorageStackedChart } from "@/components/insights/StorageStackedChart";
import { InsightsEmptyState } from "@/components/insights/InsightsEmptyState";
import { InsightsLoadingState } from "@/components/insights/InsightsLoadingState";
import { formatBytes } from "@/lib/format";
import type { InsightOverview, InsightsRange } from "@/lib/api";
import type { StorageSeriesInput } from "@/lib/storageSeries";
import { STORAGE_STACK } from "@/lib/insightsStorage";
import { gcIndicatorCards, type GcIndicatorCard } from "@/lib/gcIndicators";
import { formatUnits, ocrCostCards, originColorClass, originLabel, RANGE_PHRASE, type OcrCostCard } from "@/lib/ocrCost";
import { translationCostCards, translationEngineColorClass, translationEngineLabel } from "@/lib/translationCost";
import { useShallow } from "zustand/react/shallow";

const integerFormatter = new Intl.NumberFormat("pt-BR");

function formatRenderSeconds(v: number | null): string {
  if (v == null) return "—";
  return `${v.toFixed(1)} s`;
}

function formatCount(v: number): string {
  return integerFormatter.format(v);
}

function formatPercent(v: number | null): string {
  if (v == null) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function ratioOrNull(value: number, total: number): number | null {
  if (total <= 0) return null;
  return value / total;
}

/**
 * Os cards de armazenamento, um por lugar da arquitetura.
 *
 * O backend entrega todos prontos: a tela não soma, não subtrai e não infere
 * nenhum. A versão anterior derivava "Outros objetos" e "Outros artefatos"
 * subtraindo réguas que mediam coisas diferentes, com clamp em zero para o
 * resultado negativo não aparecer. A pilha do gráfico mora em
 * `lib/insightsStorage` — desde a F-GC.6 ela é mais fina que os cards (a
 * pirâmide aparece separada da preparação, e a miniatura, dos originais).
 */
const STORAGE_CATEGORIES = [
  {
    key: "originals_bytes_total",
    label: "Arquivos originais",
    accent: "emerald",
    context:
      "O que a plataforma enviou. Sai quando o arquivo expira; abrir de novo faz a plataforma reenviar.",
  },
  {
    key: "masks_local_bytes_total",
    label: "Máscaras no disco",
    accent: "amber",
    context:
      "A pirâmide de cada arquivo renderizado e a preparação. Acima do limite do disco, sai primeiro a do arquivo usado há mais tempo.",
  },
  {
    key: "ready_images_bytes_total",
    label: "Imagens prontas",
    accent: "cyan",
    context: "O que a tela mostra. Expira em 3 dias e se refaz sozinho na próxima abertura.",
  },
  {
    key: "separations_bytes_total",
    label: "Separações do Esko",
    accent: "rose",
    context:
      "Artes de separação preparadas para o visualizador. Vencem por desuso; refazer é uma renderização inteira.",
  },
  {
    key: "documents_local_bytes_total",
    label: "Documentos no cache quente",
    accent: "amber",
    context: "Preparação, semântica e páginas rasterizadas de documentos. Vencem após 30 dias de desuso e podem ser reidratadas da origem durável.",
  },
  {
    key: "cold_bytes_total",
    label: "Na nuvem (frio)",
    accent: "blue",
    context: "Máscaras aposentadas na nuvem. Reabrir traz de volta sem re-renderizar.",
  },
  {
    key: "database_bytes_total",
    label: "Banco de dados",
    accent: "default",
    context: "Cadastro dos arquivos, notas, histórico e a fila de render.",
  },
] as const satisfies ReadonlyArray<{
  key: keyof InsightOverview & `${string}_bytes_total`;
  label: string;
  accent: "default" | "emerald" | "blue" | "amber" | "cyan" | "rose";
  context: string;
}>;

/** A borda de cada card do disco (F-GC.6): fixa, nunca muda com o valor. */
const GC_CARD_ACCENT: Record<GcIndicatorCard["key"], "default" | "emerald" | "blue" | "amber" | "cyan" | "rose"> = {
  disk: "blue",
  retention: "emerald",
  expired: "amber",
  exists_false: "cyan",
  cold_opens: "rose",
};

/** A borda de cada card do custo com OCR: fixa, como as do disco. */
const OCR_CARD_ACCENT: Record<OcrCostCard["key"], "default" | "emerald" | "blue" | "amber" | "cyan" | "rose"> = {
  month: "amber",
  range: "blue",
  previous_month: "default",
  auto: "emerald",
};

interface BreakdownItem {
  label: string;
  value: number;
  colorClass: string;
}

interface BreakdownPanelProps {
  title: string;
  total: number;
  items: BreakdownItem[];
  footer?: string;
  formatValue?: (v: number) => string;
}

function BreakdownPanel({ title, total, items, footer, formatValue = formatBytes }: BreakdownPanelProps) {
  return (
    <div className="rounded-lg border border-ink-700 bg-ink-900 p-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm font-medium text-ink-200">{title}</p>
        <p className="shrink-0 text-sm font-semibold tabular-nums text-ink-100">
          {formatValue(total)}
        </p>
      </div>

      <div className="mt-4 space-y-3">
        {items.map((item) => {
          const pct = ratioOrNull(item.value, total);
          const width = pct == null ? "0%" : `${Math.max(2, Math.min(100, pct * 100))}%`;
          return (
            <div key={item.label} aria-label={`${item.label}: ${formatValue(item.value)}`}>
              <div className="mb-1 flex items-center justify-between gap-3 text-xs">
                <span className="min-w-0 truncate text-ink-400">{item.label}</span>
                <span className="shrink-0 tabular-nums text-ink-300">
                  {formatValue(item.value)}
                  {pct != null && <span className="text-ink-600"> · {(pct * 100).toFixed(1)}%</span>}
                </span>
              </div>
              <div className="h-2 rounded-full bg-ink-800">
                <div
                  className={`h-full rounded-full ${item.colorClass}`}
                  style={{ width: item.value > 0 ? width : "0%" }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {footer && <p className="mt-4 text-xs text-ink-500">{footer}</p>}
    </div>
  );
}

function SectionTitle({ title, meta }: { title: string; meta?: string }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-2">
      <h2 className="text-sm font-semibold text-ink-100">{title}</h2>
      {meta && <p className="text-xs text-ink-500">{meta}</p>}
    </div>
  );
}

export function Insights() {
  const {
    range,
    overview,
    timeseries,
    ocr,
    translation,
    loading,
    collecting,
    error,
    setRange,
    refresh,
    collectNow,
  } = useInsightsStore(
    useShallow((state) => ({
      range: state.range,
      overview: state.overview,
      timeseries: state.timeseries,
      ocr: state.ocr,
      translation: state.translation,
      loading: state.loading,
      collecting: state.collecting,
      error: state.error,
      setRange: state.setRange,
      refresh: state.refresh,
      collectNow: state.collectNow,
    })),
  );

  useEffect(() => {
    void refresh();
    // only on mount and range changes — refresh is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasData = overview !== null;
  const isEmpty = hasData && overview.snapshot_count === 0;
  const showSkeleton = loading && !hasData;
  const showError = error !== null && !hasData;

  const handleRangeChange = (r: InsightsRange) => {
    setRange(r);
  };

  // Uma faixa por lugar, na ordem da pilha, com "Outros" fininho no topo.
  // Os valores são os que o backend mediu — nada é somado nem subtraído aqui.
  const storageSeries: StorageSeriesInput[] = STORAGE_STACK.map((band) => ({
    key: band.key,
    label: band.label,
    color: band.color,
    points: timeseries[band.key]?.points ?? [],
  }));
  const gcCards = overview ? gcIndicatorCards(overview) : [];
  // O custo com OCR vem do livro-razão, não dos snapshots: existe mesmo quando
  // a coleta horária nunca rodou, e por isso a seção não depende do overview.
  const ocrCards = ocr ? ocrCostCards(ocr, range) : [];
  const ocrOrigins: BreakdownItem[] = ocr
    ? ocr.by_origin.map((item) => ({
        label: originLabel(item.origin),
        value: item.units,
        colorClass: originColorClass(item.origin),
      }))
    : [];
  // A tradução do Ler Texto (TR1) tem o livro-razão dela; a seção só aparece
  // quando o backend já a serve.
  const translationCards = translation ? translationCostCards(translation, range) : [];
  const translationEngines: BreakdownItem[] = translation
    ? translation.by_engine.map((item) => ({
        label: translationEngineLabel(item.engine),
        value: item.calls,
        colorClass: translationEngineColorClass(item.engine),
      }))
    : [];

  const durationBandsTotal = overview
    ? overview.renders_under_10s_count +
      overview.renders_10_30s_count +
      overview.renders_30_60s_count +
      overview.renders_over_60s_count
    : 0;
  const durationBands: BreakdownItem[] = overview
    ? [
        { label: "Até 10s", value: overview.renders_under_10s_count, colorClass: "bg-emerald-400" },
        { label: "10–30s", value: overview.renders_10_30s_count, colorClass: "bg-blue-400" },
        { label: "30–60s", value: overview.renders_30_60s_count, colorClass: "bg-amber-300" },
        { label: "Acima de 60s", value: overview.renders_over_60s_count, colorClass: "bg-rose-300" },
      ]
    : [];

  const queueRenderBandsTotal = overview
    ? overview.queue_render_under_10s_count +
      overview.queue_render_10_30s_count +
      overview.queue_render_30_60s_count +
      overview.queue_render_over_60s_count
    : 0;
  const queueRenderBands: BreakdownItem[] = overview
    ? [
        { label: "Até 10s", value: overview.queue_render_under_10s_count, colorClass: "bg-emerald-400" },
        { label: "10–30s", value: overview.queue_render_10_30s_count, colorClass: "bg-blue-400" },
        { label: "30–60s", value: overview.queue_render_30_60s_count, colorClass: "bg-amber-300" },
        { label: "Acima de 60s", value: overview.queue_render_over_60s_count, colorClass: "bg-rose-300" },
      ]
    : [];

  return (
    <section className="flex h-full min-h-0 flex-col overflow-auto px-6 py-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink-100">Insights</h1>
          <p className="mt-1 text-sm text-ink-400">Como o render vai e onde está o espaço em disco.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <InsightsRangePicker value={range} onChange={handleRangeChange} />
          <button
            type="button"
            onClick={() => void collectNow()}
            disabled={loading || collecting}
            className="flex items-center gap-1.5 rounded-lg border border-emerald-800/60 bg-emerald-950/30 px-3 py-1.5 text-xs font-medium text-emerald-200 transition-colors hover:border-emerald-600 hover:text-emerald-100 disabled:opacity-50"
          >
            <svg
              className={`h-3.5 w-3.5 ${collecting ? "animate-spin" : ""}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 7c0 1.657 3.582 3 8 3s8-1.343 8-3-3.582-3-8-3-8 1.343-8 3Z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 7v5c0 1.657 3.582 3 8 3s8-1.343 8-3V7M4 12v5c0 1.657 3.582 3 8 3s8-1.343 8-3v-5"
              />
            </svg>
            {collecting ? "Coletando" : "Coletar agora"}
          </button>
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={loading || collecting}
            className="flex items-center gap-1.5 rounded-lg border border-ink-700 bg-ink-850 px-3 py-1.5 text-xs font-medium text-ink-300 transition-colors hover:border-ink-600 hover:text-ink-100 disabled:opacity-50"
          >
            <svg
              className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            Atualizar
          </button>
        </div>
      </div>

      <div className="mt-6 flex-1">
        {/* Loading skeleton */}
        {showSkeleton && <InsightsLoadingState />}

        {/* Error state */}
        {showError && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <svg
              className="h-10 w-10 text-red-500"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
              />
            </svg>
            <p className="text-sm font-medium text-red-400">Erro ao carregar dados</p>
            <p className="max-w-xs text-xs text-ink-500">{error}</p>
            <button
              type="button"
              onClick={() => void refresh()}
              className="mt-1 rounded-lg border border-ink-700 bg-ink-850 px-4 py-2 text-xs font-medium text-ink-300 hover:text-ink-100"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {/* Empty state */}
        {isEmpty && <InsightsEmptyState />}

        {/* Main content */}
        {hasData && !isEmpty && (
          <div className="space-y-8">
            <section className="space-y-3">
              <SectionTitle title="Operação" />
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <KpiCard
                  label="Tempo médio de render"
                  value={formatRenderSeconds(overview.avg_render_seconds)}
                  accent="emerald"
                />
                <KpiCard
                  label="Arquivos na última hora"
                  value={formatCount(overview.renders_last_hour)}
                  subLabel={`${formatCount(overview.renders_count)} no período`}
                  accent="blue"
                />
                <KpiCard
                  label="Taxa de sucesso"
                  value={formatPercent(overview.success_rate)}
                  subLabel={`${formatCount(overview.renders_success_count)} arquivos renderizados`}
                  accent="cyan"
                />
                <KpiCard
                  label="Falhas"
                  value={formatCount(overview.renders_failure_count)}
                  subLabel="Arquivos com falha no desfecho"
                  accent={overview.renders_failure_count > 0 ? "rose" : "default"}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <BreakdownPanel
                  title="Distribuição do tempo de render"
                  total={durationBandsTotal}
                  items={durationBands}
                  formatValue={formatCount}
                  footer="Renders concluídos no período, por faixa de duração (só a execução)."
                />
                <BreakdownPanel
                  title="Distribuição fila + render"
                  total={queueRenderBandsTotal}
                  items={queueRenderBands}
                  formatValue={formatCount}
                  footer="Da entrada na fila até a conclusão — a espera completa que o usuário sente."
                />
              </div>
            </section>

            {ocr && (
              <section className="space-y-3">
                <SectionTitle
                  title="Custo com OCR"
                  meta="Google Vision do Modo Texto · unidade = uma imagem enviada · mês em UTC"
                />
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                  {ocrCards.map((card) => (
                    <KpiCard
                      key={card.key}
                      label={card.label}
                      value={card.value}
                      subLabel={card.subLabel}
                      accent={OCR_CARD_ACCENT[card.key]}
                    />
                  ))}
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <BreakdownPanel
                    title={`Por quem pediu, ${RANGE_PHRASE[range]}`}
                    total={ocr.units_in_range}
                    items={ocrOrigins}
                    formatValue={formatUnits}
                    footer="Chamadas ao Vision por origem da análise. «Antes do registro» é o que a migração reconstruiu das análises antigas, que não sabem quem pediu. A estimativa em dólares usa o preço e a franquia de Configurações › Visualizador; a fatura do Google é o valor final."
                  />
                  <InsightsLineChart
                    title="Chamadas ao Vision por hora"
                    points={ocr.points}
                    range={range}
                    formatValue={formatUnits}
                    color="#fbbf24"
                    imputedMode="carry"
                  />
                </div>
              </section>
            )}

            {translation && (
              <section className="space-y-3">
                <SectionTitle
                  title="Tradução do Ler Texto"
                  meta="Botão «Traduzir» do gerenciador · GPT-5.4 mini com o Google de reserva · mês em UTC"
                />
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                  {translationCards.map((card) => (
                    <KpiCard
                      key={card.key}
                      label={card.label}
                      value={card.value}
                      subLabel={card.subLabel}
                      accent={OCR_CARD_ACCENT[card.key]}
                    />
                  ))}
                </div>
                {translationEngines.length ? (
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <BreakdownPanel
                      title={`Por motor, ${RANGE_PHRASE[range]}`}
                      total={translation.period.calls}
                      items={translationEngines}
                      formatValue={formatUnits}
                      footer="Chamadas ao motor de tradução: um clique em «Traduzir» que não saiu do cache vai ao GPT-5.4 mini, e ao Google quando o modelo falha ou mexe num número. A estimativa em dólares usa os preços de Configurações › Visualizador, sem franquia; as faturas da OpenAI e do Google são o valor final."
                    />
                  </div>
                ) : null}
              </section>
            )}

            <section className="space-y-3">
              <SectionTitle
                title="Disco"
                meta="Última coleta; os contadores são das 24 h anteriores a ela"
              />
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-5">
                {gcCards.map((card) => (
                  <KpiCard
                    key={card.key}
                    label={card.label}
                    value={card.value}
                    subLabel={card.subLabel}
                    accent={GC_CARD_ACCENT[card.key]}
                  />
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <SectionTitle
                title="Armazenamento"
                meta={`${overview.snapshot_count} snapshot${overview.snapshot_count !== 1 ? "s" : ""} no período`}
              />
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-5">
                {STORAGE_CATEGORIES.map((category) => (
                  <KpiCard
                    key={category.key}
                    label={category.label}
                    value={formatBytes(overview[category.key])}
                    subLabel={category.context}
                    accent={category.accent}
                  />
                ))}
              </div>

              {/* Meia largura, como os gráficos de tendência operacional: em
                  tela cheia a pilha esticava e as faixas finas sumiam. */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <StorageStackedChart
                  title="Composição do armazenamento"
                  series={storageSeries}
                  range={range}
                  footer="Cada byte conta em um lugar só; a pilha é o espaço total ocupado. As três faixas da pirâmide e da preparação somam as “Máscaras no disco”. “Outros” são cópias de trabalho e resíduos — crescer aí costuma ser render travado."
                />
              </div>
            </section>

            <section className="space-y-3">
              <SectionTitle title="Tendência operacional" />
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <InsightsLineChart
                  title="Tempo médio de render (s)"
                  points={timeseries["avg_render_seconds"]?.points ?? []}
                  range={range}
                  formatValue={(v) => `${v.toFixed(1)} s`}
                  color="#34d399"
                />
                <InsightsLineChart
                  title="Arquivos renderizados por hora"
                  points={timeseries["renders_count"]?.points ?? []}
                  range={range}
                  formatValue={formatCount}
                  color="#38bdf8"
                />
              </div>
            </section>

          </div>
        )}

        {/* Overlay spinner when refreshing with existing data */}
        {(loading || collecting) && hasData && !isEmpty && (
          <div className="mt-4 flex items-center gap-2 text-xs text-ink-500">
            <svg
              className="h-3.5 w-3.5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            {collecting ? "Coletando snapshot..." : "Atualizando…"}
          </div>
        )}
      </div>
    </section>
  );
}
